// Configuracao da vitrine publica: tema, o que mostrar, itens por pagina,
// ofertas automaticas, faixa de convite do grupo, redes sociais, aviso de
// afiliado e os links compartilhaveis das paginas proprias.

import { useEffect, useState } from "react";
import { Check, Copy, Loader2, Save } from "lucide-react";
import {
  ITEMS_PER_PAGE_MAX,
  ITEMS_PER_PAGE_MIN,
  OFFER_MAX_AGE_DAYS_MAX,
  OFFER_MAX_AGE_DAYS_MIN,
  SOCIAL_LABELS,
  SOCIAL_NETWORKS,
  STOREFRONT_THEMES,
  STOREFRONT_THEME_LABELS,
  STOREFRONT_THEME_PALETTES,
  DEFAULT_STOREFRONT_SETTINGS,
  type SocialNetwork,
  type StorefrontSettings,
  type StorefrontTheme,
} from "@lucralink/shared";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useClipboard } from "@/lib/hooks/useClipboard";
import { useSaveStorefrontSettings, useStorefrontSettings } from "@/lib/hooks/useStorefrontSettings";
import { cn } from "@/lib/utils";

type ToggleKey = "showLogo" | "showImages" | "showDescriptions" | "showSearch" | "showCoupons" | "autoOffers";

const TOGGLES: Array<{ key: ToggleKey; label: string }> = [
  { key: "showLogo", label: "Mostrar logo" },
  { key: "showImages", label: "Mostrar imagens" },
  { key: "showDescriptions", label: "Mostrar descrições" },
  { key: "showSearch", label: "Mostrar busca" },
  { key: "showCoupons", label: "Mostrar cupons" },
  { key: "autoOffers", label: "Incluir ofertas recentes automaticamente" },
];

export const PUBLIC_PAGES: Array<{ path: string; label: string }> = [
  { path: "", label: "Todas as ofertas" },
  { path: "/quentes", label: "Ofertas mais quentes" },
  { path: "/hoje", label: "Promoções do dia" },
  { path: "/cupons", label: "Cupons" },
];

interface Props {
  readonly isPro: boolean;
  /** URL publica da vitrine (null sem slug). */
  readonly publicUrl: string | null;
}

type Patch = (next: Partial<StorefrontSettings>) => void;

function ThemePicker({ value, onPick }: { value: StorefrontTheme; onPick: (t: StorefrontTheme) => void }): JSX.Element {
  return (
    <div role="radiogroup" aria-label="Tema" className="grid grid-cols-2 gap-2 sm:grid-cols-4">
      {STOREFRONT_THEMES.map((theme) => (
        <button
          key={theme}
          type="button"
          role="radio"
          aria-checked={value === theme}
          onClick={() => onPick(theme)}
          className={cn("flex items-center gap-2 rounded-lg border p-2 text-sm", value === theme ? "border-primary/50 bg-primary/10" : "border-border")}
        >
          <span className="flex h-6 w-6 overflow-hidden rounded-full border border-border">
            <span className="h-full w-1/2" style={{ background: STOREFRONT_THEME_PALETTES[theme].bg }} />
            <span className="h-full w-1/2" style={{ background: STOREFRONT_THEME_PALETTES[theme].accent }} />
          </span>
          {STOREFRONT_THEME_LABELS[theme]}
        </button>
      ))}
    </div>
  );
}

function DisplayFields({ settings, patch }: { settings: StorefrontSettings; patch: Patch }): JSX.Element {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <div className="space-y-2">
        {TOGGLES.map((t) => (
          <label key={t.key} className="flex items-center gap-2 text-sm text-foreground">
            <input type="checkbox" className="h-4 w-4 accent-[hsl(var(--primary))]" checked={settings[t.key]} onChange={(e) => patch({ [t.key]: e.target.checked })} />
            {t.label}
          </label>
        ))}
      </div>
      <div className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="sf-per-page" variant="field">Itens por página ({ITEMS_PER_PAGE_MIN} a {ITEMS_PER_PAGE_MAX})</Label>
          <Input id="sf-per-page" type="number" min={ITEMS_PER_PAGE_MIN} max={ITEMS_PER_PAGE_MAX} value={settings.itemsPerPage} onChange={(e) => patch({ itemsPerPage: Number(e.target.value) })} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="sf-age" variant="field">Oferta automática vale por (dias)</Label>
          <Input id="sf-age" type="number" min={OFFER_MAX_AGE_DAYS_MIN} max={OFFER_MAX_AGE_DAYS_MAX} value={settings.offerMaxAgeDays} disabled={!settings.autoOffers} onChange={(e) => patch({ offerMaxAgeDays: Number(e.target.value) })} />
        </div>
      </div>
    </div>
  );
}

function InviteFields({ settings, patch }: { settings: StorefrontSettings; patch: Patch }): JSX.Element {
  const invite = settings.invite;
  return (
    <fieldset className="space-y-3">
      <legend className="text-sm font-medium text-foreground">Faixa de convite do grupo</legend>
      <p className="text-xs text-muted-foreground">Aparece no topo da vitrine. Sem link, a faixa não aparece.</p>
      <div className="grid gap-3 sm:grid-cols-3">
        <Input aria-label="Texto da faixa" placeholder="Entre no nosso grupo de ofertas" value={invite.text} maxLength={160} onChange={(e) => patch({ invite: { ...invite, text: e.target.value } })} />
        <Input aria-label="Texto do botão" placeholder="Entrar no grupo" value={invite.buttonText} maxLength={40} onChange={(e) => patch({ invite: { ...invite, buttonText: e.target.value } })} />
        <Input aria-label="Link do grupo" type="url" placeholder="https://chat.whatsapp.com/..." value={invite.url ?? ""} onChange={(e) => patch({ invite: { ...invite, url: e.target.value } })} />
      </div>
    </fieldset>
  );
}

function SocialFields({ settings, patch }: { settings: StorefrontSettings; patch: Patch }): JSX.Element {
  const set = (network: SocialNetwork, url: string): void => patch({ socials: { ...settings.socials, [network]: url } });
  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-medium text-foreground">Redes sociais no rodapé</legend>
      <div className="grid gap-2 sm:grid-cols-2">
        {SOCIAL_NETWORKS.map((network) => (
          <Input key={network} aria-label={SOCIAL_LABELS[network]} type="url" placeholder={`${SOCIAL_LABELS[network]} (https://...)`} value={settings.socials[network] ?? ""} onChange={(e) => set(network, e.target.value)} />
        ))}
      </div>
    </fieldset>
  );
}

function PublicLinks({ publicUrl }: { publicUrl: string }): JSX.Element {
  const { copiedKey, failed, copy } = useClipboard();
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-foreground">Links para compartilhar</p>
      <ul className="space-y-1.5">
        {PUBLIC_PAGES.map((page) => {
          const url = `${publicUrl}${page.path}`;
          return (
            <li key={page.path} className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-1.5 text-xs">
              <span className="min-w-0 truncate">
                <strong className="text-foreground">{page.label}:</strong> <a className="text-primary hover:underline" href={url} target="_blank" rel="noreferrer">{url}</a>
              </span>
              <Button type="button" size="icon-sm" variant="ghost" aria-label={`Copiar link ${page.label}`} onClick={() => void copy(url, url)}>
                {copiedKey === url ? <Check size={12} /> : <Copy size={12} />}
              </Button>
            </li>
          );
        })}
      </ul>
      {failed && <p role="alert" className="text-xs text-destructive">Não foi possível copiar. Selecione o link e copie manualmente.</p>}
    </div>
  );
}

export function VitrineSettingsCard({ isPro, publicUrl }: Props): JSX.Element {
  const { data, isError } = useStorefrontSettings();
  const save = useSaveStorefrontSettings();
  const [settings, setSettings] = useState<StorefrontSettings>(DEFAULT_STOREFRONT_SETTINGS);
  const [message, setMessage] = useState<{ text: string; ok: boolean } | null>(null);
  useEffect(() => {
    if (data) setSettings(data);
  }, [data]);
  const patch: Patch = (next) => setSettings((prev) => ({ ...prev, ...next }));

  async function handleSave(): Promise<void> {
    try {
      await save.mutateAsync(settings);
      setMessage({ text: "Configuração da vitrine salva. A página pública atualiza em até 1 minuto.", ok: true });
    } catch (e) {
      setMessage({ text: e instanceof Error ? e.message : "Não foi possível salvar.", ok: false });
    }
  }

  return (
    <Card variant="raised" className="overflow-hidden">
      <CardHeader className="border-b border-border bg-[hsl(var(--surface-raised)/0.45)]">
        <CardTitle>Aparência e páginas</CardTitle>
        <CardDescription>Tema, o que aparece, faixa de convite, redes sociais e aviso de afiliado.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6 pt-5">
        {isError && <p role="alert" className="text-sm text-destructive">Não foi possível carregar a configuração atual.</p>}
        <ThemePicker value={settings.theme} onPick={(theme) => patch({ theme })} />
        <DisplayFields settings={settings} patch={patch} />
        <InviteFields settings={settings} patch={patch} />
        <SocialFields settings={settings} patch={patch} />
        <div className="space-y-1.5">
          <Label htmlFor="sf-notice" variant="field">Aviso de afiliado (rodapé)</Label>
          <textarea id="sf-notice" rows={2} maxLength={300} value={settings.affiliateNotice} onChange={(e) => patch({ affiliateNotice: e.target.value })} className="flex w-full rounded-md border bg-[hsl(var(--input))] px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
        </div>
        {publicUrl && <PublicLinks publicUrl={publicUrl} />}
        {message && <p role={message.ok ? "status" : "alert"} className={message.ok ? "text-sm text-primary" : "text-sm text-destructive"}>{message.text}</p>}
        <Button type="button" disabled={!isPro || save.isPending} onClick={() => void handleSave()}>
          {save.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Salvar aparência
        </Button>
        {!isPro && <p className="text-xs text-muted-foreground">Personalizar a vitrine faz parte do plano Pro.</p>}
      </CardContent>
    </Card>
  );
}
